import React, { useState, useEffect, useContext, useRef } from 'react';
import { Box, TextField, Typography, CircularProgress } from '@mui/material';
import useDebounce from '../../../../../../../hooks/useDebounce';
import { COLORS } from '../../../../../../../constants/radar';
import { COMMENTS_REQUIRED, MIN_COMMENTS_LENGTH } from '../../../../../../../constants/assignmentConstants';

// Create a context to track the saving state (imported from parent)
const DirtyStateContext = React.createContext({
  isDirty: false,
  setIsDirty: () => {},
  savingFieldId: null,
  setSavingFieldId: () => {},
});

// Separate component to handle comments with debouncing
const CommentsField = ({ initialComments = '', onCommentChange, disabled, skillId, reviewerId, required = true, minContentsLength = MIN_COMMENTS_LENGTH, fieldId, placeholder = 'Add your comments about this skill...', rows = 4 }) => {
  const [comments, setComments] = useState(initialComments);
  const { isDirty: isGlobalDirty, setIsDirty: setGlobalDirty, savingFieldId, setSavingFieldId } = useContext(DirtyStateContext);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saving', 'saved', 'changed'
  
  // This field should be disabled if another field is saving (but not if this field is saving)
  const isOtherFieldSaving = savingFieldId && savingFieldId !== fieldId;
  
  // Use the existing useDebounce hook to debounce the comments value
  const debouncedComment = useDebounce(comments, 1000);

  // Track if the last change was from user input (vs external prop change)
  const isUserInputRef = useRef(false);

  // Initialize comments from props - mark as external change to prevent save
  useEffect(() => {
    isUserInputRef.current = false; // Mark as external change
    setComments(initialComments);
    setSaveStatus('saved');
  }, [initialComments, skillId, reviewerId]);

  // Handle user typing in the text field
  const handleChange = (e) => {
    const newValue = e.target.value;
    isUserInputRef.current = true; // Mark as user input
    setComments(newValue);
    
    // Immediately mark this field as active when user starts typing
    if (!savingFieldId) {
      setSavingFieldId(fieldId);
    }
    
    if(newValue !== initialComments) {
      setGlobalDirty(true);
      setSaveStatus('changed');
    } else {
      setSaveStatus('saved');
      setGlobalDirty(false);
      // If back to original value, clear the saving field
      if (savingFieldId === fieldId) {
        setSavingFieldId(null);
      }
    }
  };

  // This effect will run only when debouncedComment changes (1 second after typing stops)
  useEffect(() => {
    // Only save if the change came from user input (not external prop change)
    // and the comment has actually changed
    if (isUserInputRef.current && debouncedComment !== initialComments) {
      setSaveStatus('saving');
      // Field ID is already set when typing started, don't override it

      onCommentChange(debouncedComment)
        .then(() => {
          setSaveStatus('saved');
          setGlobalDirty(false);
          setSavingFieldId(null); // Clear the saving field after successful save
        })
        .catch(() => {
          setSaveStatus('error');
          setGlobalDirty(false);
          setSavingFieldId(null); // Clear the saving field on error too
          // Keep dirty state to allow retry
        });
    }
  }, [debouncedComment, onCommentChange, setGlobalDirty, setSavingFieldId, fieldId]);

  return (
    <>
      <TextField
        fullWidth
        multiline
        rows={rows}
        variant="outlined"
        placeholder={placeholder}
        value={comments}
        onChange={handleChange}
        disabled={disabled || isOtherFieldSaving} // Only disable if competencyLevelId === defaultLevelId OR if another field is saving
        sx={{
          backgroundColor: '#fff',
          position: 'relative',
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              transition: 'all 0.2s ease',
            },
            minHeight: rows < 4 ? 'auto' : '80px',
            backgroundColor: disabled || isOtherFieldSaving ? '#f5f5f5' : '#fff',
          },
          '& .MuiInputBase-input': {
            fontSize: '0.875rem',
          },
          '& .Mui-disabled': {
            WebkitTextFillColor: 'rgba(0, 0, 0, 0.38)',
            fontStyle: 'italic',
          },
        }}
        slotProps={{
          input: {
            endAdornment: (
              <>
                <Box
                  sx={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    pointerEvents: 'none',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    zIndex: 1,
                  }}
                >
                  {required ? `${comments.length}/${minContentsLength}+` : (comments.length > 0 ? comments.length : '')}
                </Box>
                {!disabled && !isOtherFieldSaving && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: '8px',
                      right: '8px',
                      padding: '4px 8px',
                      fontSize: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      pointerEvents: 'none',
                      borderRadius: '4px',
                      color: COLORS.PRIMARY,
                      backgroundColor: COLORS.BACKGROUND_LIGHT,
                    }}
                  >
                    {saveStatus === 'error' && 'Error saving'}
                    {saveStatus === 'saving' && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Saving
                        <CircularProgress 
                          size={12} 
                          thickness={4} 
                          sx={{ 
                            ml: 0.8,
                            color: COLORS.PRIMARY_LIGHT
                          }} 
                        />
                      </Box>
                    )}
                    {saveStatus === 'changed' && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Typing 
                        <CircularProgress 
                          size={12} 
                          thickness={4} 
                          sx={{ 
                            ml: 0.8,
                            color: COLORS.PRIMARY_LIGHT
                          }} 
                        />
                      </Box>
                    )}
                    {saveStatus === 'saved' && 'Saved'}
                  </Box>
                )}
              </>
            ),
          },
        }}
      />
      {required && (
        <Typography 
          variant="caption" 
          sx={{ 
            mt: 0.5, 
            color: 'text.secondary',
            fontSize: '0.75rem'
          }}
        >
          Minimum {minContentsLength} character{minContentsLength !== 1 ? 's' : ''}
        </Typography>
      )}
    </>
  );
};

export { CommentsField, DirtyStateContext, COMMENTS_REQUIRED, MIN_COMMENTS_LENGTH }; 